import { useLayoutEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import axios from 'axios';
import { Icon } from '../components/Icon.tsx';
import { useAuthStore } from '../store/auth.ts';
import { dv } from '../i18n/dv.ts';

export function Login(): JSX.Element {
  const navigate = useNavigate();
  const login = useAuthStore((s) => s.login);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  // Login is always shown in light theme regardless of the user's dark-mode
  // preference; their real theme is restored when navigating away.
  useLayoutEffect(() => {
    const html = document.documentElement;
    html.classList.remove('dark');
    return () => {
      try {
        const stored = localStorage.getItem('wapstat.theme');
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        if (stored === 'dark' || (!stored && prefersDark)) html.classList.add('dark');
      } catch {
        /* ignore */
      }
    };
  }, []);

  async function submit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    setPending(true);
    try {
      await login(email, password);
      await navigate({ to: '/' });
    } catch (e) {
      if (axios.isAxiosError(e) && e.response?.status === 429) {
        setError(dv.common.error);
      } else if (axios.isAxiosError(e) && e.response?.status === 401) {
        setError(dv.auth.invalidCredentials);
      } else {
        setError(dv.common.error);
      }
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Brand panel — desktop only */}
      <aside className="hidden lg:flex relative flex-col justify-between overflow-hidden bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] p-12">
        {/* Decorative tonal wash */}
        <div
          aria-hidden
          className="absolute inset-0 opacity-25 pointer-events-none"
          style={{
            backgroundImage:
              'radial-gradient(ellipse 50rem 35rem at 15% 110%, hsl(var(--primary-container)), transparent 60%)',
          }}
        />

        <div className="relative flex items-center gap-3">
          <img
            src="/icons/wapstat-mark.svg"
            alt=""
            className="size-9 rounded-md object-contain ring-1 ring-[hsl(var(--primary-foreground)/0.15)]"
          />
          <span className="font-serif text-3xl leading-none tracking-tight">{dv.appName}</span>
        </div>

        <div className="relative max-w-md space-y-6">
          <h1 className="font-serif text-5xl xl:text-6xl leading-[1.05] !text-[hsl(var(--primary-foreground))] tracking-tight">
            {dv.appName}
          </h1>
        </div>

        <div className="relative flex items-end justify-between gap-6">
          <p className="mono-meta !text-[hsl(var(--primary-foreground)/0.55)] max-w-xs leading-relaxed">
            Wapplify · {new Date().getFullYear()}
          </p>
          <svg
            aria-hidden
            viewBox="0 0 80 80"
            className="size-16 text-[hsl(var(--primary-foreground)/0.2)] shrink-0"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
          >
            <circle cx="40" cy="40" r="35" />
            <circle cx="40" cy="40" r="28" />
            <path d="M22 42 l10 10 l26 -26" strokeWidth="2.5" />
          </svg>
        </div>
      </aside>

      {/* Form panel */}
      <section className="flex items-center justify-center px-4 py-10 sm:py-16">
        <div className="w-full max-w-sm space-y-8">
          {/* Compact brand on mobile */}
          <div className="lg:hidden flex items-center justify-center gap-2.5">
            <img
              src="/icons/wapstat-mark.svg"
              alt=""
              className="size-8 rounded-md object-contain ring-1 ring-border"
            />
            <span className="font-serif text-2xl leading-none text-foreground tracking-tight">{dv.appName}</span>
          </div>

          <header className="space-y-2 text-center lg:text-start">
            <h2 className="font-serif text-4xl leading-none text-foreground tracking-tight">
              {dv.auth.login}
            </h2>
          </header>

          <form className="space-y-5" onSubmit={submit}>
            <div className="space-y-1.5">
              <label htmlFor="email" className="status-label !text-foreground/80">
                {dv.auth.email}
              </label>
              <input
                id="email"
                type="email"
                dir="ltr"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input"
              />
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <label htmlFor="password" className="status-label !text-foreground/80">
                  {dv.auth.password}
                </label>
                <a
                  href="https://office.wapplify.app/forgot-password"
                  className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-4"
                >
                  {dv.auth.forgotPassword}
                </a>
              </div>
              <div className="relative">
                <input
                  id="password"
                  type={showPw ? 'text' : 'password'}
                  dir="ltr"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input pe-11"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((v) => !v)}
                  aria-label={showPw ? dv.auth.hidePassword : dv.auth.showPassword}
                  className="absolute inset-y-0 end-0 inline-flex w-10 items-center justify-center text-muted-foreground hover:text-foreground"
                >
                  <Icon name={showPw ? 'visibility_off' : 'visibility'} size={16} />
                </button>
              </div>
            </div>

            {error && (
              <div
                role="alert"
                className="rounded-xl bg-[hsl(var(--error-container))] px-3 py-2.5 text-sm text-[hsl(var(--on-error-container))]"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={pending}
              className="btn-primary w-full py-2.5"
            >
              {pending ? dv.common.loading : (<>{dv.auth.login} <Icon name="arrow_back" size={16} /></>)}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
